import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import {
  Calendar,
  Clock,
  Loader2,
  CheckCircle,
  AlertCircle,
  Plus,
  Trash2,
  Save,
  RotateCcw,
  Settings,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import DashboardFooter from "@/components/DashboardFooter";
import TimezoneField from "@/pages/integrations/shared/TimezoneField";
import { getDetectedTimezone } from "@/pages/integrations/shared/timezoneUtils";
import { getClientAuthHeaders, getClientAuthToken } from "@/lib/apiAuth";
import {
  DEFAULT_BOT_SCHEDULE,
  DEFAULT_WEEKDAY_QUIET_INTERVAL,
  WEEKDAY_KEYS,
  WEEKDAY_LABELS,
  normalizeBotSchedule,
} from "@/constants/botScheduleDefaults";
import { DASHBOARD_ROUTES } from "@/lib/dashboardRoutes";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SETTINGS_URL = `${BACKEND_URL}/api/bot/settings`;
const SAVE_REAL_ESTATE_URL = `${BACKEND_URL}/api/bot/save_settings`;
const SAVE_ROOFING_URL = `${BACKEND_URL}/api/bot/save_settings/roofing_bot`;
const SAVE_RENOVATION_URL = `${BACKEND_URL}/api/bot/save_settings/renovation_bot`;

const ToggleSwitch = ({ enabled, onChange, disabled = false, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={enabled}
    disabled={disabled}
    onClick={() => onChange(!enabled)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#00D1FF]/50 ${
      disabled ? "opacity-50 cursor-not-allowed" : ""
    } ${enabled ? "bg-[#00D1FF]" : "bg-white/10"}`}
    data-testid={testId}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${
        enabled ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

const IntervalRow = ({ interval, onChange, onRemove, disabled, testIdPrefix }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid={`${testIdPrefix}-interval`}>
    <input
      type="time"
      value={interval.start || ""}
      onChange={(e) => onChange({ ...interval, start: e.target.value })}
      disabled={disabled}
      className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] text-sm font-body focus:outline-none focus:border-[#00D1FF]/50 disabled:opacity-40"
      data-testid={`${testIdPrefix}-start`}
    />
    <span className="text-[#9CA3AF] text-sm">–</span>
    <input
      type="time"
      value={interval.end || ""}
      onChange={(e) => onChange({ ...interval, end: e.target.value })}
      disabled={disabled}
      className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] text-sm font-body focus:outline-none focus:border-[#00D1FF]/50 disabled:opacity-40"
      data-testid={`${testIdPrefix}-end`}
    />
    <button
      type="button"
      onClick={onRemove}
      disabled={disabled}
      className="p-2 rounded-lg text-[#9CA3AF] hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40"
      aria-label="Remove interval"
      data-testid={`${testIdPrefix}-remove`}
    >
      <Trash2 className="w-4 h-4" />
    </button>
  </div>
);

const DayScheduleRow = ({ dayKey, day, onChange }) => {
  const label = WEEKDAY_LABELS[dayKey];
  const active = !!day.is_active;
  const intervals = day.intervals || [];

  const updateDay = (patch) => onChange({ ...day, ...patch });

  const updateInterval = (index, value) => {
    const next = [...intervals];
    next[index] = value;
    updateDay({ intervals: next });
  };

  const addInterval = () => {
    updateDay({ intervals: [...intervals, { ...DEFAULT_WEEKDAY_QUIET_INTERVAL }] });
  };

  const removeInterval = (index) => {
    updateDay({ intervals: intervals.filter((_, i) => i !== index) });
  };

  const toggleActive = () => {
    if (active) {
      onChange({ is_active: false, intervals: [] });
    } else {
      onChange({
        is_active: true,
        intervals: intervals.length ? intervals : [{ ...DEFAULT_WEEKDAY_QUIET_INTERVAL }],
      });
    }
  };

  return (
    <div
      className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-3"
      data-testid={`bot-schedule-day-${dayKey}`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[#F3F4F6] text-sm font-body font-medium w-28">{label}</span>
        <ToggleSwitch
          enabled={active}
          onChange={toggleActive}
          testId={`bot-schedule-day-${dayKey}-toggle`}
        />
        <span className="text-[#9CA3AF] text-xs font-body">
          {active ? "Quiet hours on" : "Available all day"}
        </span>
        {active && (
          <button
            type="button"
            onClick={addInterval}
            className="ml-auto inline-flex items-center gap-1 text-xs text-[#00D1FF] hover:text-[#00D1FF]/80 font-body"
            data-testid={`bot-schedule-day-${dayKey}-add-interval`}
          >
            <Plus className="w-3.5 h-3.5" />
            Add quiet hours
          </button>
        )}
      </div>
      {active && (
        <div className="space-y-2 pl-0 sm:pl-32">
          {intervals.length === 0 ? (
            <p className="text-[#9CA3AF] text-xs font-body">
              No quiet hours — the Digital Brain can respond all day on this weekday.
            </p>
          ) : (
            intervals.map((iv, idx) => (
              <IntervalRow
                key={idx}
                interval={iv}
                onChange={(v) => updateInterval(idx, v)}
                onRemove={() => removeInterval(idx)}
                disabled={false}
                testIdPrefix={`bot-schedule-day-${dayKey}-${idx}`}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

const OverrideCard = ({ override, onChange, onRemove, testIdPrefix }) => {
  const active = !!override.is_active;
  const intervals = override.intervals || [];

  const update = (patch) => onChange({ ...override, ...patch });

  const updateInterval = (index, value) => {
    const next = [...intervals];
    next[index] = value;
    update({ intervals: next });
  };

  return (
    <div
      className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-3"
      data-testid={testIdPrefix}
    >
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[140px]">
          <label className="block text-[#9CA3AF] text-xs font-body mb-1">Date</label>
          <input
            type="date"
            value={override.date || ""}
            onChange={(e) => update({ date: e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] text-sm font-body focus:outline-none focus:border-[#00D1FF]/50"
            data-testid={`${testIdPrefix}-date`}
          />
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="block text-[#9CA3AF] text-xs font-body mb-1">Reason</label>
          <input
            type="text"
            value={override.reason || ""}
            onChange={(e) => update({ reason: e.target.value })}
            placeholder="e.g. Public holiday"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] text-sm font-body focus:outline-none focus:border-[#00D1FF]/50"
            data-testid={`${testIdPrefix}-reason`}
          />
        </div>
        <div className="flex items-center gap-2 pb-1">
          <ToggleSwitch
            enabled={active}
            onChange={(v) =>
              update(
                v
                  ? {
                      is_active: true,
                      intervals:
                        intervals.length > 0 ? intervals : [{ start: "10:00", end: "14:00" }],
                    }
                  : { is_active: false, intervals: [] }
              )
            }
            testId={`${testIdPrefix}-toggle`}
          />
          <span className="text-[#9CA3AF] text-xs font-body">
            {active ? "Custom quiet hours" : "Quiet all day"}
          </span>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="p-2 rounded-lg text-[#9CA3AF] hover:text-red-400 hover:bg-red-500/10"
          aria-label="Remove override"
          data-testid={`${testIdPrefix}-remove`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {active && (
        <div className="space-y-2">
          {intervals.map((iv, idx) => (
            <IntervalRow
              key={idx}
              interval={iv}
              onChange={(v) => updateInterval(idx, v)}
              onRemove={() => update({ intervals: intervals.filter((_, i) => i !== idx) })}
              disabled={false}
              testIdPrefix={`${testIdPrefix}-interval-${idx}`}
            />
          ))}
          <button
            type="button"
            onClick={() => update({ intervals: [...intervals, { start: "10:00", end: "14:00" }] })}
            className="inline-flex items-center gap-1 text-xs text-[#00D1FF] font-body"
          >
            <Plus className="w-3.5 h-3.5" />
            Add quiet hours
          </button>
        </div>
      )}
    </div>
  );
};

function compareTime(start, end) {
  if (!start || !end) return true;
  return start < end;
}

function validateBotSchedule(schedule) {
  const errors = [];
  const weekly = schedule.weekly_schedule || {};

  for (const key of WEEKDAY_KEYS) {
    const day = weekly[key];
    if (!day?.is_active) continue;
    for (const iv of day.intervals || []) {
      if (iv.start && iv.end && !compareTime(iv.start, iv.end)) {
        errors.push(`${WEEKDAY_LABELS[key]}: start must be before end (${iv.start} – ${iv.end})`);
      }
    }
  }

  const dates = new Set();
  for (const o of schedule.overrides || []) {
    if (o.date) {
      if (dates.has(o.date)) {
        errors.push(`Duplicate override date: ${o.date}`);
      }
      dates.add(o.date);
    }
    if (o.is_active) {
      for (const iv of o.intervals || []) {
        if (iv.start && iv.end && !compareTime(iv.start, iv.end)) {
          errors.push(`Override ${o.date || "(no date)"}: start must be before end`);
        }
      }
    }
  }

  return errors;
}

function buildRealEstateSavePayload(fullSettings, botSchedule) {
  const kb = fullSettings.knowledge_base || {};
  const hs = kb.handoff_settings || {};
  const ql = fullSettings.qualification_logic || {};
  const sch = fullSettings.scheduling || {};
  const tov = fullSettings.tone_of_voice || {};

  return {
    bot_type: fullSettings.bot_type || "real_estate_v1",
    knowledge_base: {
      agency_name: kb.agency_name || "",
      website_url: kb.website_url || "",
      handoff_settings: {
        lead_agent_name: hs.lead_agent_name || "",
        lead_agent_role: hs.lead_agent_role || "",
        lead_agent_phone: hs.lead_agent_phone || "",
        lead_agent_email: hs.lead_agent_email || "",
        allowed_channels: hs.allowed_channels || [],
      },
      vendor_faqs: (kb.vendor_faqs || []).map((f) => ({
        topic: (f.topic || "").trim(),
        details: (f.details || "").trim(),
      })),
    },
    qualification_logic: {
      buyer_questions: ql.buyer_questions || [],
      vendor_questions: ql.vendor_questions || [],
      tenant_questions: ql.tenant_questions || [],
      questions: ql.questions || [],
      unqualified_action: ql.unqualified_action || "manager",
      strict_qualification_gate: !!ql.strict_qualification_gate,
    },
    scheduling: {
      inspection_hours: sch.inspection_hours || "",
      business_hours: sch.business_hours,
      meeting_type: sch.meeting_type,
      default_duration_minutes: sch.default_duration_minutes,
      daylight_rule_enforced: sch.daylight_rule_enforced,
      buffer_between_meetings: sch.buffer_between_meetings,
      team_calendars: sch.team_calendars,
      bot_schedule: botSchedule,
    },
    tone_of_voice: {
      bot_name: tov.bot_name || "",
      style: tov.style || "",
      use_emojis: !!tov.use_emojis,
      use_aussie_slang: !!tov.use_aussie_slang,
    },
  };
}

function buildRoofingSavePayload(fullSettings, botSchedule) {
  return {
    bot_type: "roofing_v1",
    knowledge_base: fullSettings.knowledge_base || {},
    qualification_logic: fullSettings.qualification_logic || {},
    scheduling: {
      ...(fullSettings.scheduling || {}),
      bot_schedule: botSchedule,
    },
    tone_of_voice: fullSettings.tone_of_voice || {},
  };
}

function buildRenovationSavePayload(fullSettings, botSchedule) {
  return {
    bot_type: "renovation_v1",
    knowledge_base: fullSettings.knowledge_base || {},
    qualification_logic: fullSettings.qualification_logic || {},
    scheduling: {
      ...(fullSettings.scheduling || {}),
      bot_schedule: botSchedule,
    },
    tone_of_voice: fullSettings.tone_of_voice || {},
  };
}

const BotSchedule = () => {
  const [botSchedule, setBotSchedule] = useState(() =>
    normalizeBotSchedule(DEFAULT_BOT_SCHEDULE)
  );
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [fullSettings, setFullSettings] = useState(null);
  const [hasSettings, setHasSettings] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [apiError, setApiError] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  const detectedTimezone = useMemo(() => getDetectedTimezone(), []);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    setApiError("");
    try {
      const headers = getClientAuthHeaders();

      const res = await axios.get(SETTINGS_URL, { headers });
      const data = res.data || {};

      if (!data.has_settings || !data.settings) {
        setHasSettings(false);
        setFullSettings(null);
        const normalized = normalizeBotSchedule(DEFAULT_BOT_SCHEDULE);
        setBotSchedule(normalized);
        setSavedSnapshot(JSON.stringify(normalized));
        return;
      }

      setHasSettings(true);
      setFullSettings(data.settings);
      const stored = data.settings?.scheduling?.bot_schedule;
      const normalized = normalizeBotSchedule(stored);
      setBotSchedule(normalized);
      setSavedSnapshot(JSON.stringify(normalized));
    } catch (err) {
      console.error("Load bot schedule:", err);
      setApiError(
        err.response?.data?.detail || err.message || "Failed to load Digital Brain settings."
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const updateWeeklyDay = (dayKey, day) => {
    setBotSchedule((prev) => ({
      ...prev,
      weekly_schedule: {
        ...prev.weekly_schedule,
        [dayKey]: day,
      },
    }));
    setSaveSuccess(false);
  };

  const updateOverride = (index, value) => {
    setBotSchedule((prev) => {
      const next = [...(prev.overrides || [])];
      next[index] = value;
      return { ...prev, overrides: next };
    });
    setSaveSuccess(false);
  };

  const addOverride = () => {
    setBotSchedule((prev) => ({
      ...prev,
      overrides: [
        ...(prev.overrides || []),
        { date: "", is_active: false, intervals: [], reason: "" },
      ],
    }));
    setSaveSuccess(false);
  };

  const removeOverride = (index) => {
    setBotSchedule((prev) => ({
      ...prev,
      overrides: (prev.overrides || []).filter((_, i) => i !== index),
    }));
    setSaveSuccess(false);
  };

  const handleCancel = () => {
    if (savedSnapshot) {
      setBotSchedule(normalizeBotSchedule(JSON.parse(savedSnapshot)));
    }
    setValidationErrors([]);
    setApiError("");
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    if (!hasSettings || !fullSettings) {
      setApiError("Configure Digital Brain Settings before saving a schedule.");
      return;
    }

    const errors = validateBotSchedule(botSchedule);
    setValidationErrors(errors);
    if (errors.length > 0) return;

    setIsSaving(true);
    setApiError("");
    setSaveSuccess(false);

    try {
      const headers = { "Content-Type": "application/json", ...getClientAuthHeaders() };

      const botType =
        fullSettings.bot_type || Cookies.get("bot_type") || "real_estate_v1";
      const isRoofing = botType === "roofing_v1";
      const isRenovation = botType === "renovation_v1";

      const payload = isRoofing
        ? buildRoofingSavePayload(fullSettings, botSchedule)
        : isRenovation
          ? buildRenovationSavePayload(fullSettings, botSchedule)
          : buildRealEstateSavePayload(fullSettings, botSchedule);

      const url = isRoofing
        ? SAVE_ROOFING_URL
        : isRenovation
          ? SAVE_RENOVATION_URL
          : SAVE_REAL_ESTATE_URL;
      const res = await axios.post(url, payload, { headers });

      if (res.data?.status === "ok") {
        const mergedSettings = {
          ...fullSettings,
          scheduling: {
            ...(fullSettings.scheduling || {}),
            bot_schedule: botSchedule,
          },
        };
        setFullSettings(mergedSettings);
        setSavedSnapshot(JSON.stringify(botSchedule));
        setSaveSuccess(true);
      } else {
        setApiError(res.data?.detail || res.data?.message || "Save failed.");
      }
    } catch (err) {
      console.error("Save bot schedule:", err);
      setApiError(
        err.response?.data?.detail || err.message || "Failed to save bot schedule."
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0F1115] flex">
        <Sidebar />
        <div className="flex-1 md:ml-64 flex flex-col min-h-screen">
          <div className="flex-1 flex items-center justify-center px-6">
            <Loader2 className="w-10 h-10 text-[#00D1FF] animate-spin" />
          </div>
          <DashboardFooter />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1115] flex" data-testid="bot-schedule-page">
      <Sidebar />
      <div className="flex-1 md:ml-64">
        <main className="pt-8 pb-16 px-6 max-w-3xl mx-auto">
          <div className="mb-8">
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6] mb-2 flex items-center gap-3">
              <Calendar className="w-8 h-8 text-[#00D1FF]" />
              Digital Brain Schedule
            </h1>
            <p className="text-[#9CA3AF] font-body text-sm">
              Set when your Digital Brain is off duty. Outside those quiet hours, it can respond to leads.
            </p>
          </div>

          {!hasSettings && (
            <div
              className="mb-6 p-4 rounded-xl border border-amber-500/30 bg-amber-500/10 flex gap-3"
              data-testid="bot-schedule-no-settings"
            >
              <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[#F3F4F6] font-body text-sm mb-2">
                  Configure Digital Brain Settings first before saving a schedule.
                </p>
                <Link
                  to={DASHBOARD_ROUTES.settings}
                  className="inline-flex items-center gap-2 text-[#00D1FF] text-sm font-body hover:underline"
                >
                  <Settings className="w-4 h-4" />
                  Go to Digital Brain Settings
                </Link>
              </div>
            </div>
          )}

          {apiError && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm font-body flex gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {typeof apiError === "string" ? apiError : JSON.stringify(apiError)}
            </div>
          )}

          {validationErrors.length > 0 && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm font-body">
              <ul className="list-disc list-inside space-y-1">
                {validationErrors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          {saveSuccess && (
            <div
              className="mb-4 p-3 rounded-lg bg-[#00D1FF]/10 border border-[#00D1FF]/30 text-[#00D1FF] text-sm font-body flex gap-2"
              data-testid="bot-schedule-save-success"
            >
              <CheckCircle className="w-4 h-4" />
              Schedule saved successfully.
            </div>
          )}

          <div className="glass rounded-2xl p-6 border border-white/10 space-y-8 mb-6">
            <section>
              <h2 className="text-[#F3F4F6] font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#00D1FF]" />
                General
              </h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                  <div>
                    <p className="text-[#F3F4F6] text-sm font-body font-medium">Schedule enabled</p>
                    <p className="text-[#9CA3AF] text-xs font-body">
                      When on, quiet hours below are enforced. When off, the Digital Brain can respond anytime.
                    </p>
                  </div>
                  <ToggleSwitch
                    enabled={!!botSchedule.enabled}
                    onChange={(v) => {
                      setBotSchedule((p) => ({ ...p, enabled: v }));
                      setSaveSuccess(false);
                    }}
                    testId="bot-schedule-enabled"
                  />
                </div>

                <TimezoneField
                  value={botSchedule.timezone || ""}
                  onChange={(tz) => {
                    setBotSchedule((p) => ({ ...p, timezone: tz }));
                    setSaveSuccess(false);
                  }}
                  variant="google"
                  detectedTimezone={detectedTimezone}
                  label="Timezone"
                  required={false}
                  testIdPrefix="bot-schedule-timezone"
                  footerHintDefault="Time zone for weekly quiet hours and date overrides"
                />
              </div>
            </section>

            <section>
              <h2 className="text-[#F3F4F6] font-heading font-semibold text-lg mb-2">
                Weekly quiet hours
              </h2>
              <p className="text-[#9CA3AF] text-sm font-body mb-2 leading-relaxed">
                For each day you enable, add time ranges when the Digital Brain will not respond. If a day is
                off, the Digital Brain can respond all day (when schedule is enabled and no closing override
                applies).
              </p>
              <p className="text-[#9CA3AF]/80 text-xs font-body mb-4">
                Default for weekdays: 8:00 AM–4:00 PM quiet hours. Weekends: available all day.
              </p>
              <div className="space-y-3">
                {WEEKDAY_KEYS.map((key) => (
                  <DayScheduleRow
                    key={key}
                    dayKey={key}
                    day={botSchedule.weekly_schedule?.[key] || { is_active: false, intervals: [] }}
                    onChange={(day) => updateWeeklyDay(key, day)}
                  />
                ))}
              </div>
            </section>

            <section>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-[#F3F4F6] font-heading font-semibold text-lg">
                  Date overrides
                </h2>
                <button
                  type="button"
                  onClick={addOverride}
                  className="inline-flex items-center gap-1 text-sm text-[#00D1FF] font-body hover:underline"
                  data-testid="bot-schedule-add-override"
                >
                  <Plus className="w-4 h-4" />
                  Add override
                </button>
              </div>
              <p className="text-[#9CA3AF] text-sm font-body mb-4 leading-relaxed">
                Set quiet hours or a full-day closure for specific dates. Overrides take priority
                over the weekly schedule.
              </p>
              <div className="space-y-3">
                {(botSchedule.overrides || []).length === 0 ? (
                  <p className="text-[#9CA3AF] text-sm font-body">No overrides configured.</p>
                ) : (
                  botSchedule.overrides.map((o, idx) => (
                    <OverrideCard
                      key={idx}
                      override={o}
                      onChange={(v) => updateOverride(idx, v)}
                      onRemove={() => removeOverride(idx)}
                      testIdPrefix={`bot-schedule-override-${idx}`}
                    />
                  ))
                )}
              </div>
            </section>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || !hasSettings}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#00D1FF] text-[#0F1115] font-heading font-semibold text-sm hover:bg-[#00D1FF]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="bot-schedule-save"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save schedule
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-white/20 text-[#F3F4F6] font-body text-sm hover:bg-white/5 disabled:opacity-50 transition-colors"
              data-testid="bot-schedule-cancel"
            >
              <RotateCcw className="w-4 h-4" />
              Cancel
            </button>
          </div>
        </main>
        <DashboardFooter />
      </div>
    </div>
  );
};

export default BotSchedule;
